import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader } from '../core';
import styles from './user-list.module.scss';
import _ from 'lodash';
import { UserDetailsComponent } from '../user-details';
import { useSearchParams } from 'react-router-dom';
import { formatFullName, formatNumber, normalizedContains, removeDiacritics } from 'shared/utils';
import { Button, ScreenTitle, SearchBox, Table } from '../design-system';
import { usePaginationSearchParams } from '../design-system/table/hooks';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { getLang } from 'shared/store/selectors/lang.selector';
import { isCollaborator, isAccountAdmin } from 'shared/store/selectors/auth.selector';
import { EmailListFilter } from './email-list-filter';

const MULTI_ROW_SELECTION = {
  mode: 'multiRow',
  checkboxes: true,
  headerCheckbox: true,
  selectAll: 'filtered',
};

const tableContainerProps = { className: styles.table };

const getRowId = params => params.data.id;

const UserList = React.memo(function UserList({
  users,
  columns,
  onDelete,
  onBulkDelete,
  onTransferOwnership,
  openNotification,
  notificationContextHolder,
  pageTitle,
  buttonText,
  onAdd = null,
  bulkUploadButton = null,
  details,
  baseRoute,
  detailsRouteQueryParam,
  analyticsTriggers,
  multiSelect = false,
  openPreview = null,
  clearSelection = null,
  showEmailListFilter = false,
  presetEmailFilter,
  onEmailFilterClear,
}) {
  const lang = useSelector(getLang('USERS_MANAGEMENT'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [userDetails, setUserDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [emailFilterList, setEmailFilterList] = useState(presetEmailFilter ?? []);
  // The preset loads async (e.g. from a link), so apply it once it arrives.
  const [appliedPreset, setAppliedPreset] = useState(presetEmailFilter);
  if (presetEmailFilter !== appliedPreset) {
    setAppliedPreset(presetEmailFilter);
    setEmailFilterList(presetEmailFilter ?? []);
  }
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [displayedSelectedCount, setDisplayedSelectedCount] = useState(0);
  const tableRef = useRef(null);
  const [resultCount, setResultCount] = useState(users?.length ?? 0);
  const isAllowedToAdd = useSelector(isCollaborator);
  const isAdminUser = useSelector(isAccountAdmin);

  const clearSelectedRows = useCallback(() => {
    tableRef.current?.getApi?.()?.deselectAll();
  }, []);

  const openDetails = useCallback(
    id => {
      analyticsTriggers?.openDetails({ id });

      if (openPreview) {
        openPreview(id);
      } else {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set(detailsRouteQueryParam, id);
          return newParams;
        });
      }
    },
    [analyticsTriggers, detailsRouteQueryParam, setSearchParams, openPreview],
  );

  const closeDetails = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete(detailsRouteQueryParam);
      return newParams;
    });
    setDetailsOpen(false);
    // Clear selection when closing details panel (e.g., after successful save)
    clearSelectedRows();
  }, [detailsRouteQueryParam, setSearchParams, clearSelectedRows]);

  useEffect(() => {
    const id = searchParams.get(detailsRouteQueryParam);
    if (id) {
      setUserDetails(users?.find(user => user.id === id));
      setDetailsOpen(true);
    } else {
      setUserDetails([]);
      setDetailsOpen(false);
    }
  }, [searchParams, users, detailsRouteQueryParam]);

  const onSearchTermChanged = e => setSearchTerm(removeDiacritics(e?.target?.value));

  const emailFilterSet = useMemo(() => new Set(emailFilterList), [emailFilterList]);

  const filterAction = useCallback(
    ({ email, firstName, lastName, name }) => {
      const matchesSearch =
        normalizedContains(email, searchTerm) ||
        normalizedContains(firstName, searchTerm) ||
        normalizedContains(lastName, searchTerm) ||
        normalizedContains(name, searchTerm);
      if (!showEmailListFilter) return matchesSearch;

      const matchesEmailFilter =
        emailFilterSet.size === 0 || emailFilterSet.has(email?.toLowerCase());

      return matchesSearch && matchesEmailFilter;
    },
    [searchTerm, showEmailListFilter, emailFilterSet],
  );

  // Expose clearSelectedRows to parent via clearSelection callback
  useEffect(() => {
    if (clearSelection) {
      clearSelection(clearSelectedRows);
    }
  }, [clearSelection, clearSelectedRows]);

  const onClickAdd = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete(detailsRouteQueryParam);
      return newParams;
    });

    setDetailsOpen(true);
    setUserDetails([]);

    if (!!onAdd) {
      onAdd();
    }
  }, [onAdd, setSearchParams, detailsRouteQueryParam]);

  const onClickEdit = useCallback(() => {
    const visibleSelected = tableRef.current?.getDisplayedSelectedRows() ?? [];
    if (visibleSelected.length === 1) {
      openDetails(visibleSelected[0].id);
    }
    setUserDetails(visibleSelected);
    setDetailsOpen(true);
  }, [openDetails]);

  const onClickBulkDelete = useCallback(() => {
    if (onBulkDelete) {
      const visibleSelected = tableRef.current?.getDisplayedSelectedRows() ?? [];
      onBulkDelete(visibleSelected);
    }
  }, [onBulkDelete]);

  const filteredUsers = useMemo(
    () =>
      users
        ?.map(user => ({
          ...user,
          name: formatFullName(user, {
            labelInParenthesis: user?.isDeactivated ? lang.DEACTIVATED : null,
          }),
          ...user.professional,
        }))
        ?.filter(filterAction),
    [users, filterAction, lang.DEACTIVATED],
  );

  // Update displayed selected count
  const updateDisplayedSelectedCount = useCallback(event => {
    const api = event?.api || tableRef.current?.getApi?.();
    if (!api) {
      setDisplayedSelectedCount(0);
      return;
    }

    const selectedNodes = api.getSelectedNodes();
    const displayedSelectedNodes = selectedNodes.filter(node => node.displayed);
    setDisplayedSelectedCount(displayedSelectedNodes.length);
  }, []);

  const updateResultCount = useCallback(
    event => {
      setResultCount(event.api.getDisplayedRowCount());
      updateDisplayedSelectedCount(event);
    },
    [updateDisplayedSelectedCount],
  );

  const { initialPageSize, handleGridReady, handlePaginationChanged } = usePaginationSearchParams({
    enabled: true,
    defaultPageSize: 20,
    onGridReady: updateResultCount,
  });

  // Handle selection change for AgGrid
  const handleSelectionChanged = useCallback(
    event => {
      updateDisplayedSelectedCount(event);
    },
    [updateDisplayedSelectedCount],
  );

  if (_.isNull(users)) return <Loader />;

  return (
    <>
      {notificationContextHolder}
      {details ?? (
        <UserDetailsComponent
          user={userDetails}
          close={closeDetails}
          open={detailsOpen}
          onDeleteUser={onDelete}
          onTransferOwnership={onTransferOwnership}
          openNotification={openNotification}
        />
      )}
      <div className={classNames(['new-design-system', styles.root])}>
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            <div className={styles.title}>
              <ScreenTitle title={pageTitle} />
              {isAllowedToAdd && (
                <>
                  <Button
                    data-testid="user-management-add"
                    className={styles.button}
                    type="primary"
                    onClick={onClickAdd}>
                    {buttonText}
                  </Button>
                  {bulkUploadButton}
                </>
              )}
              {displayedSelectedCount > 0 && (
                <>
                  <Button className={styles.button} ghost type="primary" onClick={onClickEdit}>
                    {lang.EDIT_USER.replace('{number}', formatNumber(displayedSelectedCount) ?? '')}
                  </Button>
                  {isAdminUser && (
                    <Button
                      className={styles.button}
                      danger
                      onClick={onClickBulkDelete}
                      data-testid="user-management-bulk-delete">
                      {lang.DELETE_USERS?.replace(
                        '{number}',
                        formatNumber(displayedSelectedCount) ?? '',
                      ) || `Delete ${displayedSelectedCount} user(s)`}
                    </Button>
                  )}
                </>
              )}
            </div>
            <div className={styles.results}>
              {resultCount > 0 && (
                <h4>{lang.SHOWING_RESULTS.replace('{count}', formatNumber(resultCount))}</h4>
              )}
            </div>
          </div>
          <div className={styles.search}>
            <div className={styles.searchRow}>
              <SearchBox
                size="large"
                data-testid="user-management-search"
                className={styles.input}
                onChange={onSearchTermChanged}
                placeholder={lang.SEARCH_PLACEHOLDER}
              />
              {showEmailListFilter && (
                <EmailListFilter
                  emailFilterList={emailFilterList}
                  onApply={setEmailFilterList}
                  onClear={() => {
                    setEmailFilterList([]);
                    onEmailFilterClear?.();
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <Table
          enableActiveFilters
          enableColumnChooser
          ref={tableRef}
          containerProps={tableContainerProps}
          columnDefs={columns}
          onGridReady={handleGridReady}
          onFilterChanged={updateResultCount}
          onRowDataUpdated={updateResultCount}
          onPaginationChanged={handlePaginationChanged}
          rowData={filteredUsers}
          getRowId={getRowId}
          paginationPageSize={initialPageSize}
          rowSelection={multiSelect ? MULTI_ROW_SELECTION : undefined}
          onSelectionChanged={multiSelect ? handleSelectionChanged : undefined}
        />
      </div>
    </>
  );
});

export { UserList };
